import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cs-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-cs-surface-page disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97]',
  {
    variants: {
      variant: {
        primary:     'bg-cs-primary-500 text-white hover:bg-cs-primary-600',
        secondary:   'bg-cs-surface-raised border border-cs-border text-cs-text-primary hover:bg-cs-surface-hover hover:border-cs-border-subtle',
        ghost:       'text-cs-text-secondary hover:bg-cs-surface-hover hover:text-cs-text-primary',
        destructive: 'bg-cs-error-500 text-white hover:bg-red-600',
        outline:     'border border-cs-border bg-transparent text-cs-text-primary hover:bg-cs-surface-hover hover:border-cs-border-subtle',
        link:        'text-cs-primary-400 underline-offset-4 hover:underline p-0 h-auto',
        default:     'bg-cs-primary-500 text-white hover:bg-cs-primary-600',
      },
      size: {
        default: 'h-8 px-4 py-2',
        sm:      'h-7 px-3 text-xs',
        lg:      'h-10 px-5',
        icon:    'h-8 w-8',
        'icon-sm': 'h-7 w-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || disabled}
        aria-busy={loading}
        {...props}
      >
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
